import React, { useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import ReactPixel from "react-facebook-pixel";

import Loader from "../common/Loader";
import RaffleByCampus from "./RaffleByCampus";
import RaffleClosed from "./raffleStates/RaffleClosed";
import RaffleFAQs from "./general/RaffleFAQs";
import RaffleNavBar from "./general/RaffleNavBar";
import RafflePurchaseComplete from "./general/RafflePurchaseComplete";
import Raffle404 from "./general/Raffle404";

import RaffleApi from "../../services/resources/RaffleApi";
import axios from "axios";

const { REACT_APP_FACEBOOK_PIXEL_ID } = process.env;

export default function RaffleWrapper(props) {
  //STATE
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [raffle, setRaffle] = useState({});
  const [raffleEnrollment, setRaffleEnrollment] = useState({});
  const [raffleWinners, setRaffleWinners] = useState([]);
  const [sellerId, setSellerId] = useState(null);
  const [sellerList, setSellerList] = useState([]);
  const [sellerListError, setSellerListError] = useState(false);
  const [sellerListLoading, setSellerListLoading] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(null);
  const [team, setTeam] = useState(null);

  const apiSignal = axios.CancelToken.source();

  const {
    history,
    location: { pathname, search },
    match: {
      path,
      params: { sellerSlug, teamSlug },
    },
  } = props;

  const {
    isActive: raffleIsActive,
    isPastResultsPosting,
    gregorianYear,
    hebrewYear,
  } = raffle || {};

  const {
    campaignLogoUrl,
    campusName,
    chabadHouseName,
    isActive: raffleEnrollmentIsActive,
  } = raffleEnrollment || {};

  useEffect(() => {
    getRaffleDetails();
    return () => apiSignal.cancel();
    //eslint-disable-next-line
  }, []);

  const getSellerIdFromSlug = (sellerList, sellerSlug) => {
    const sellerIndex = sellerList
      .map((seller) => seller.slug)
      .indexOf(sellerSlug);

    if (sellerIndex >= 0) {
      return sellerList[sellerIndex].id;
    }
    return null;
  };

  const getRaffleDetails = async () => {
    setLoading(true);

    try {
      //get raffle event info
      const raffle = await RaffleApi.getRaffle(apiSignal.token);
      setRaffle(raffle);

      let _sellerList = [];
      //get raffle enrollment info
      if (!sellerList.length) {
        _sellerList = await getSellerList();
        setSellerList(_sellerList);
      }

      let sellerId = null;
      if (sellerSlug) {
        sellerId = getSellerIdFromSlug(_sellerList, sellerSlug);
      }
      setSellerId(sellerId);

      const raffleEnrollment = await RaffleApi.getRaffleEnrollment(
        apiSignal.token,
        sellerId
      );
      setRaffleEnrollment(raffleEnrollment);

      //if team slug in url, set team data
      if (raffleEnrollment.referrers && teamSlug) {
        const _team = raffleEnrollment.referrers.find(
          (team) => team.slug === teamSlug.toLowerCase()
        );
        if (!!_team) {
          await RaffleApi.getDonors(
            apiSignal.token,
            sellerId,
            1,
            24,
            "DateDesc",
            "",
            _team.id
          );
          setTeam(_team);
        } else if (teamSlug !== "purchase-complete") {
          history.replace(`/${sellerSlug}`);
        }
      }

      //get raffle winners if posted
      if (raffle.isPastResultsPosting) {
        const raffleWinners = await RaffleApi.getRaffleWinners(
          apiSignal.token,
          raffleEnrollment.chabadHouseID
        );
        setRaffleWinners(raffleWinners);
      }

      //initialize facebook pixel (for coci seller)
      facebookPixelInit(raffleEnrollment);
    } catch (err) {
      if (!axios.isCancel(err)) {
        const is404 = (err.response && err.response.status) === 404;
        //Is this correct?
        setError(!is404 || "404");
      }
    }
    setLoading(false);
  };

  const getSellerList = async () => {
    setSellerListLoading(true);

    let sellerList = [];
    try {
      const sellers = await RaffleApi.getSellers(apiSignal.token);
      sellerList = sellers.map((seller, index) => ({
        key: index,
        value: [seller.sellerName, seller.campusName]
          .filter((name) => name)
          .join(" - "),
        ...seller,
      }));
      setSellerList(sellerList);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setSellerListError(true);
      }
    }
    setSellerListLoading(false);

    return sellerList;
  };

  //track with facebook pixel for coci-seller-specific-page only (url /chabad-on-campus-international)
  const hasFacebookPixelTracking = (raffleEnrollment) => {
    return !!(
      REACT_APP_FACEBOOK_PIXEL_ID &&
      sellerId &&
      raffleEnrollment.isCociSeller
    );
  };

  const facebookPixelInit = (raffleEnrollment) => {
    try {
      if (hasFacebookPixelTracking(raffleEnrollment)) {
        const advancedMatching = {};
        const options = { autoConfig: true };
        ReactPixel.init(REACT_APP_FACEBOOK_PIXEL_ID, advancedMatching, options);
        ReactPixel.pageView(); // Track page view
      }
    } catch (e) {
      console.log("Error initializing facebook pixel", e);
    }
  };

  const termsAndConditions = {
    cookiePolicy: "cookiePolicy",
    legalText: "legalText",
    privacyPolicy: "privacyPolicy",
  };

  const toggleShowTermsAndConditions = (event, termsType) => {
    event.preventDefault();
    setShowTermsAndConditions(termsType || null);
  };

  const basePath = sellerId ? path : "";
  const homeUrl = `/${sellerId ? `${sellerSlug}` : ""}${
    team ? `/${teamSlug}` : ""
  }`;

  const hideCopy = search.includes("copy=false");

  const isRaffleActive = !!(raffleIsActive && raffleEnrollmentIsActive);

  return (
    <React.Fragment>
      {loading ? (
        <div className="full-page-loader">
          <Loader />
        </div>
      ) : error ? (
        error === "404" ? (
          <div className="full-page-state">
            <img src="/images/closed.svg" alt="closed" />
            <span className="medium-text">
              There is no current Grand Draw. Please check back here another
              time.
            </span>
          </div>
        ) : (
          <div className="full-page-state">
            <img src="/images/error.svg" alt="error" />
            <span className="error-text medium-text">
              Our server is being stubborn, sorry for the inconvenience. Please
              try again.
            </span>
          </div>
        )
      ) : (
        <>
          <div className="relative">
            <Switch>
              <Route
                exact
                path={`${basePath}/faq`}
                render={() => (
                  <RaffleFAQs
                    raffleEnrollment={raffleEnrollment}
                    sellerId={sellerId}
                    goBack={props.history.goBack}
                    toggleShowTermsAndConditions={toggleShowTermsAndConditions}
                    termsAndConditions={termsAndConditions}
                  />
                )}
              />
              {!isRaffleActive && (
                <Route
                  path="*"
                  render={() => (
                    <RaffleClosed
                      raffle={raffle}
                      raffleEnrollment={raffleEnrollment}
                      sellerId={sellerId}
                      isPastResultsPosting={isPastResultsPosting}
                      raffleWinners={raffleWinners}
                    />
                  )}
                />
              )}
              {isRaffleActive && (
                <Route
                  exact
                  path={`${basePath}/purchase-complete`}
                  render={(routerProps) => (
                    <RafflePurchaseComplete
                      {...routerProps}
                      campaignLogoUrl={campaignLogoUrl}
                      campusName={campusName}
                      chabadHouseName={chabadHouseName}
                      homeUrl={homeUrl}
                      sellerId={sellerId}
                      gregorianYear={gregorianYear}
                      hebrewYear={hebrewYear}
                    />
                  )}
                />
              )}
              {isRaffleActive && (
                <Route
                  exact
                  path={basePath}
                  render={(routerProps) => (
                    <RaffleByCampus
                      {...routerProps}
                      hideCopy={hideCopy}
                      raffle={raffle}
                      raffleEnrollment={raffleEnrollment}
                      team={team}
                      sellerId={sellerId}
                      sellerList={{
                        error: sellerListError,
                        loading: sellerListLoading,
                        sellers: sellerList,
                      }}
                      termsAndConditions={termsAndConditions}
                      toggleShowTermsAndConditions={
                        toggleShowTermsAndConditions
                      }
                      updateRaffleEnrollment={setRaffleEnrollment}
                    />
                  )}
                />
              )}
              {isRaffleActive && (
                <Route
                  exact
                  path={`${basePath}/*`}
                  render={() => (
                    <Raffle404
                      homeUrl={homeUrl}
                      campaignLogoUrl={campaignLogoUrl}
                      gregorianYear={gregorianYear}
                      hebrewYear={hebrewYear}
                    />
                  )}
                />
              )}
            </Switch>
          </div>
          <RaffleNavBar
            homeUrl={homeUrl}
            isRaffleActive={isRaffleActive}
            pathname={pathname}
            raffleEnrollment={raffleEnrollment}
            sellerId={sellerId}
            showTermsAndConditions={showTermsAndConditions}
            termsAndConditions={termsAndConditions}
            toggleShowTermsAndConditions={toggleShowTermsAndConditions}
          />
        </>
      )}
    </React.Fragment>
  );
}
